import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getFighter } from '../services/CMSService';
import analytics from '../services/analytics';
import FighterTitlesGrid from './FighterTitlesGrid';
import './FighterDetailPage.css';

const parseCountryImg = raw => {
  try {
    return JSON.parse(raw || '{}')?.url || null;
  } catch {
    return null;
  }
};

const FighterDetailPage = () => {
  const { id } = useParams();
  const [fighter, setFighter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFighterData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getFighter(id);
        if (!data) {
          setError('Fighter not found');
        } else {
          setFighter(data);
          analytics.trackFighterView({ id, ...data, ...(data.fullFighter || {}) });
        }
      } catch (err) {
        console.error('Error fetching fighter:', err);
        setError('Failed to load fighter data');
      } finally {
        setLoading(false);
      }
    };

    fetchFighterData();
  }, [id]);

  if (loading) {
    return (
      <div
        className="fighter-page"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div className="fighter-content__spinner" />
      </div>
    );
  }

  if (error || !fighter) {
    return (
      <div className="fighter-page">
        <div className="fighter-page__error">
          <h2>Fighter Not Found</h2>
          <p>{error || 'Unable to load fighter information.'}</p>
          <Link to="/fighters" className="fighter-page__back">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Fighters
          </Link>
        </div>
      </div>
    );
  }

  const full = fighter.fullFighter || {};
  const heroBg = full.hero_image_url || '';
  const bodyImage = full.body_image_url || '';
  const countryFlag = parseCountryImg(full.country_img);
  const countryLabel = full.nationality || full.country_code || '';
  const hasCountry = Boolean(countryFlag || countryLabel);

  return (
    <div className="fighter-page">
      {/* ── Hero Section ── */}
      <div className="fighter-hero" style={heroBg ? { '--hero-bg': `url(${heroBg})` } : undefined}>
        <div className="fighter-hero__watermark" aria-hidden="true">
          Bare Knuckle Fighting
        </div>

        {bodyImage && (
          <img
            src={bodyImage}
            alt={fighter.title}
            className="fighter-hero__body"
            onError={e => {
              e.target.style.display = 'none';
            }}
          />
        )}

        <div className="fighter-hero__inner">
          <Link to="/fighters" className="fighter-page__back fighter-hero__back">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Fighters
          </Link>

          <div className="fighter-hero__content">
            {(fighter.champion || hasCountry) && (
              <div className="fighter-hero__meta">
                {fighter.champion && (
                  <div className="fighter-hero__champion-badge">
                    <svg className="fighter-hero__belt-icon" viewBox="0 0 40 22" fill="#0a0a0a">
                      <path d="M0 8h13v6H0z" />
                      <path d="M27 8h13v6H27z" />
                      <ellipse cx="20" cy="11" rx="10" ry="10.5" />
                      <ellipse cx="20" cy="11" rx="6.2" ry="6.8" fill="#d9b53f" />
                      <path
                        d="M16.6 12.7v-2.8l1.8 1.4 1.6-2.3 1.6 2.3 1.8-1.4v2.8z"
                        fill="#0a0a0a"
                      />
                    </svg>
                    <span className="fighter-hero__champion-text">
                      {full.weight_class
                        ? `${full.weight_class.toUpperCase()} CHAMPION`
                        : 'CHAMPION'}
                    </span>
                  </div>
                )}

                {hasCountry && (
                  <div className="fighter-hero__country">
                    {countryFlag && (
                      <img src={countryFlag} alt="" className="fighter-hero__country-flag" />
                    )}
                    {countryLabel && (
                      <span className="fighter-hero__country-text">{countryLabel}</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <h1 className="fighter-hero__name">{fighter.title}</h1>

            <div className="fighter-hero__stats">
              <div className="fighter-hero__stat-box">
                <span className="fighter-hero__stat-value">{fighter.wins}</span>
                <span className="fighter-hero__stat-label">Wins</span>
              </div>
              <span className="fighter-hero__stat-dash">–</span>
              <div className="fighter-hero__stat-box">
                <span className="fighter-hero__stat-value">{fighter.losses}</span>
                <span className="fighter-hero__stat-label">Losses</span>
              </div>
              <span className="fighter-hero__stat-dash">–</span>
              <div className="fighter-hero__stat-box">
                <span className="fighter-hero__stat-value">{fighter.draws}</span>
                <span className="fighter-hero__stat-label">Draws</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Fighter titles (search-style grid + hover preview) ── */}
      <FighterTitlesGrid fighterName={fighter.title} />
    </div>
  );
};

export default FighterDetailPage;
